import type {
  APIComponentType,
  ArdoqId,
  FormattingFilter,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { filterInterface } from '@ardoq/filter-interface';
import { ComponentTypeInfo } from '../types';
import { getComponentTypes, getScopeComponentTypes } from '../utils/modelUtils';
import {
  ensureContrast,
  getDefaultCSSColor,
  getLightenedColor,
  tryParseColorStringToHex,
} from '@ardoq/color-helpers';
import { vizFilterLightGray } from '@ardoq/design-tokens';

export type LegendComponentType = Pick<
  APIComponentType,
  'name' | 'icon' | 'image'
> & {
  ids: string[];
  borderColor?: string;
  backgroundColor?: string;
  isSelected?: boolean;
};

type LegendComponentTypesReducerState = {
  result: LegendComponentType[];
  keyedResults: Map<string, LegendComponentType>;
};

const legendComponentTypeKey = (
  componentType: APIComponentType,
  backgroundColor: string,
  borderColor: string
) =>
  `${backgroundColor}~~${borderColor}~~${
    componentType.image || componentType.icon
  }~~${componentType.name}`;

export const getLegendComponentTypeColor = (componentType: APIComponentType) =>
  // make sure the colors are in the same format to avoid duplicating components in the legend
  tryParseColorStringToHex(componentType?.color) ||
  getDefaultCSSColor(componentType.level);

const standardLegendComponentTypeColors = (componentType: APIComponentType) => {
  const color = getLegendComponentTypeColor(componentType);
  const backgroundColor = getLightenedColor(color);
  return {
    backgroundColor,
    borderColor: ensureContrast(backgroundColor, color),
  };
};
const legendComponentTypeColors = (
  componentType: APIComponentType,
  formattingFilters: FormattingFilter[]
) =>
  !formattingFilters.length
    ? standardLegendComponentTypeColors(componentType)
    : formattingFilters.find(
          ({ name, value }) => name === 'type' && value === componentType.name
        )
      ? { backgroundColor: '', borderColor: '' } // UX requirement: color swatch is totally hidden if there's a relevant filter. the swatch will be displayed in the formatting legend row
      : {
          backgroundColor: vizFilterLightGray,
          borderColor: ensureContrast(
            vizFilterLightGray,
            getLegendComponentTypeColor(componentType)
          ),
        };

const getComponentTypesForLegend = (
  componentTypes: ComponentTypeInfo[],
  formattingFilters: FormattingFilter[]
) =>
  componentTypes.reduce(
    (state: LegendComponentTypesReducerState, { componentType }) => {
      const { backgroundColor, borderColor } = legendComponentTypeColors(
        componentType,
        formattingFilters
      );
      const distinctKey = legendComponentTypeKey(
        componentType,
        backgroundColor,
        borderColor
      );
      const existing = state.keyedResults.get(distinctKey);
      if (existing) {
        existing.ids.push(componentType.id);
      } else {
        const { id, name, color, icon, image } = componentType;
        const newEntry = {
          ids: [id],
          name,
          color,
          icon,
          image,
          backgroundColor,
          borderColor,
        };
        state.result.push(newEntry);
        state.keyedResults.set(distinctKey, newEntry);
      }
      return state;
    },
    { result: [], keyedResults: new Map() }
  ).result;

type ScopeDataArgs = {
  enhancedScopeData: EnhancedScopeData;
  formattingFilters: FormattingFilter[];
};

export const getComponentTypesInfo = (
  componentIds: ArdoqId[],
  scopeDataArgs?: ScopeDataArgs
) => {
  const componentTypes = scopeDataArgs
    ? getScopeComponentTypes(scopeDataArgs.enhancedScopeData, componentIds)
    : getComponentTypes(componentIds);
  const legendComponentTypes = getComponentTypesForLegend(
    componentTypes,
    scopeDataArgs?.formattingFilters ?? filterInterface.getFormattingFilters()
  );
  return { componentTypes, legendComponentTypes };
};
